/**
 * 应用入口：路由表 + 登录态守卫。
 * 登录/注册是独立鉴权表面（不含 Shell）；其余 4 个 tab 共用 App Shell。
 */
import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { fetchMe, tokenStore } from './api/auth';
import { APP_BASE } from './api/client';
import { Shell } from './components/Shell';
import { ToastHost } from './components/ui';
import { ConsolePage } from './pages/Console';
import { CommunityPage } from './pages/Community';
import { IntegrationsPage } from './pages/Integrations';
import { LoginPage } from './pages/Login';
import { McpPage } from './pages/Mcp';

function BootScreen() {
  return (
    <div className="app">
      <main className="app-content" style={{ paddingTop: 24 }}>
        <div className="skeleton" style={{ width: 120, height: 20, marginBottom: 16 }} />
        <div className="stack" style={{ gap: 12 }}>
          <div className="skeleton skeleton--card" />
          <div className="skeleton skeleton--card" />
        </div>
      </main>
    </div>
  );
}

function RequireAuth() {
  const token = tokenStore.get();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, enabled: !!token, retry: false });

  useEffect(() => {
    if (me.isError) tokenStore.clear();
  }, [me.isError]);

  if (!token) return <Navigate to="/login" replace />;
  if (me.isError) return <Navigate to="/login" replace />;
  if (!me.data) return <BootScreen />;
  return <Outlet />;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  if (tokenStore.get()) return <Navigate to="/console" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter basename={APP_BASE}>
      <ToastHost />
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfAuthed>
              <LoginPage />
            </RedirectIfAuthed>
          }
        />
        <Route element={<RequireAuth />}>
          <Route
            path="/console"
            element={
              <Shell>
                <ConsolePage />
              </Shell>
            }
          />
          <Route
            path="/community"
            element={
              <Shell>
                <CommunityPage />
              </Shell>
            }
          />
          <Route
            path="/mcp"
            element={
              <Shell>
                <McpPage />
              </Shell>
            }
          />
          <Route
            path="/integrations"
            element={
              <Shell>
                <IntegrationsPage />
              </Shell>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/console" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
